import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Box, Typography, Grid, IconButton, Card, CardContent, useTheme, Button, GlobalStyles, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title, TimeScale } from 'chart.js';
import mockData from './mock-data.json';
import ChartCardWithFilters from './ChartCardWithFilters';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BarChartIcon from '@mui/icons-material/BarChart';
import PieChartIcon from '@mui/icons-material/PieChart';
import TimelineIcon from '@mui/icons-material/Timeline';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import RouteIcon from '@mui/icons-material/Route';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import WebIcon from '@mui/icons-material/Web';
import 'chartjs-adapter-date-fns';
import CloseIcon from '@mui/icons-material/Close';

// Register ChartJS components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  TimeScale
);
ChartJS.defaults.font.size = 14;

// Static styles to prevent re-creation on every render
const getCardStyles = (color) => ({
  height: 180,
  cursor: 'pointer',
  background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
  border: `2px solid ${color}30`,
  borderRadius: 3,
  position: 'relative',
  overflow: 'hidden',
  boxShadow: `0 4px 12px ${color}15`,
  transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(135deg, ${color}10 0%, transparent 50%, ${color}08 100%)`,
    opacity: 0,
    transition: 'opacity 0.4s ease',
    zIndex: 0
  },
  '&:hover': {
    boxShadow: `0 12px 40px ${color}25, 0 4px 20px ${color}15`,
    borderColor: `${color}50`,
    transform: 'translateY(-4px)',
    '&::before': {
      opacity: 1
    }
  }
});

const cardContentStyles = {
  height: '100%', 
  display: 'flex', 
  flexDirection: 'column', 
  alignItems: 'center', 
  justifyContent: 'center',
  p: 3,
  textAlign: 'center',
  position: 'relative'
};

const getIconStyles = (color) => ({
  fontSize: 48, 
  color: color,
  mb: 2,
  filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))',
  zIndex: 2,
  transition: 'all 0.3s ease',
  transform: 'scale(1)'
});

const getTitleStyles = (color) => ({
  fontWeight: 700,
  color: color,
  mb: 1,
  fontSize: '1.1rem',
  lineHeight: 1.3,
  zIndex: 2,
  transition: 'color 0.3s ease'
});

const subtitleStyles = {
  fontSize: '0.85rem',
  opacity: 0.8,
  zIndex: 2,
  transition: 'opacity 0.3s ease'
};

const getExpandIconStyles = (color) => ({
  position: 'absolute',
  bottom: 8,
  right: 8,
  color: color,
  opacity: 0.6,
  fontSize: 20,
  transition: 'all 0.3s ease',
  transform: 'rotate(0deg)',
  zIndex: 2
});

// Minimized card component for navbar-style layout
const NavBarCard = React.memo(({ config, isSelected, onToggle, theme }) => {
  const IconComponent = config.icon;
  
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 1,
        borderRadius: 3,
        cursor: 'pointer',
        flex: '1 1 0',
        minWidth: '105px',
        maxWidth: '150px',
        justifyContent: 'center',
        transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        background: isSelected 
          ? `linear-gradient(135deg, ${config.color}20 0%, ${config.color}10 100%)`
          : `linear-gradient(135deg, ${theme.palette.grey[50]} 0%, ${theme.palette.grey[25]} 100%)`,
        border: isSelected 
          ? `2px solid ${config.color}`
          : `2px solid ${theme.palette.grey[200]}`,
        boxShadow: isSelected 
          ? `0 8px 24px ${config.color}20`
          : '0 2px 8px rgba(0,0,0,0.05)',
        '&:hover': {
          background: isSelected 
            ? `linear-gradient(135deg, ${config.color}30 0%, ${config.color}15 100%)`
            : `linear-gradient(135deg, ${config.color}10 0%, ${config.color}05 100%)`,
          borderColor: config.color,
          boxShadow: `0 12px 32px ${config.color}25`,
          transform: 'translateY(-2px)',
          '& .nav-icon': {
            transform: 'scale(1.1)',
            filter: `drop-shadow(0 4px 8px ${config.color}30)`
          },
          '& .nav-title': {
            color: config.color,
            fontWeight: 700
          }
        }
      }}
      onClick={() => onToggle(config.id)}
    >
      <IconComponent 
        className="nav-icon"
        sx={{ 
          color: isSelected ? config.color : theme.palette.text.secondary,
          fontSize: 20,
          transition: 'all 0.3s ease',
          filter: isSelected ? `drop-shadow(0 2px 4px ${config.color}20)` : 'none'
        }} 
      />
      
      <Typography 
        className="nav-title"
        variant="body1" 
        sx={{ 
          fontWeight: isSelected ? 700 : 600,
          color: isSelected ? config.color : theme.palette.text.primary,
          fontSize: '0.85rem',
          letterSpacing: '0.2px',
          transition: 'all 0.3s ease',
          whiteSpace: 'nowrap'
        }}
      >
        {config.title}
      </Typography>
      
    </Box>
  );
});

export default function SyncHighlight({ 
  statisticsData = null,
  cartSummaryComponent = null,
  demandFilterComponent = null,
  availabilityFilterComponent = null,
  onServiceFilterComponent = null,
  waitingTimeFilterComponent = null,
  tripsFilterComponent = null,
  viewMode = 'multi',
  onViewModeChange = null,
  tripType = null,
  tripTypeOptions = [],
  onDemandFilterIconClick = null,
  onAvailabilityFilterIconClick = null,
  onOnServiceFilterIconClick = null,
  onWaitingTimeFilterIconClick = null,
  onTripFilterIconClick = null
}) {
  const theme = useTheme();
  const [expandedCards, setExpandedCards] = useState([]);
  
  // Handle viewMode changes - when switching to single mode, keep only the first expanded card
  useEffect(() => {
    if (viewMode === 'single' && expandedCards.length > 1) {
      setExpandedCards([expandedCards[0]]);
    }
  }, [viewMode, expandedCards]);


  // Use provided statistics data or fallback to mock data
  const charts = statisticsData?.charts || mockData?.charts;

  // Simple chart configuration - only include visible sections
  const chartConfigs = useMemo(() => {
    const configs = [];
    let currentIndex = 0;
    
    // Add Cart Summary as first card if provided
    if (cartSummaryComponent) {
      configs.push({
        id: 'cartSummary',
        title: 'Usage Summary',
        icon: DirectionsCarIcon,
        color: theme.palette.primary.main,
        data: null, // Cart summary has its own data handling
        filterComponent: null, // Cart summary handles its own filtering
        isCartSummary: true,
        originalIndex: currentIndex++
      });
    }
    
    // Add regular chart configurations - only for visible sections
    if (demandFilterComponent) {
      configs.push({
        id: 'demand',
        title: 'Unique Users',
        icon: BarChartIcon,
        color: theme.palette.primary.main,
        data: charts?.demand,
        filterComponent: null, // Remove filter component, will use filter icon instead
        showFilterIcon: true,
        onFilterIconClick: onDemandFilterIconClick,
        originalIndex: currentIndex++
      });
    }
    
    if (availabilityFilterComponent) {
      configs.push({
        id: 'availability',
        title: 'Availability', 
        icon: PieChartIcon,
        color: theme.palette.primary.main,
        data: charts?.availability,
        filterComponent: null, // Remove filter component, will use filter icon instead
        showFilterIcon: true,
        onFilterIconClick: onAvailabilityFilterIconClick,
        originalIndex: currentIndex++
      });
    }
    
    if (onServiceFilterComponent) {
      configs.push({
        id: 'onService',
        title: 'On Service',
        icon: DirectionsCarIcon,
        color: theme.palette.primary.main,
        data: charts?.onService,
        filterComponent: null, // Remove filter component, will use filter icon instead
        showFilterIcon: true,
        onFilterIconClick: onOnServiceFilterIconClick,
        originalIndex: currentIndex++
      });
    }
    
    if (waitingTimeFilterComponent) {
      configs.push({
        id: 'waitingTime',
        title: 'Waiting Time',
        icon: TimelineIcon,
        color: theme.palette.primary.main,
        data: charts?.waitingTime,
        filterComponent: null, // Remove filter component, will use filter icon instead
        showFilterIcon: true,
        onFilterIconClick: onWaitingTimeFilterIconClick,
        originalIndex: currentIndex++
      });
    }
    
    if (tripsFilterComponent) {
      configs.push({
        id: 'trips',
        title: 'Trips',
        icon: RouteIcon,
        color: theme.palette.primary.main,
        data: charts?.trips,
        filterComponent: null, // Remove filter component, will use filter icon instead
        showFilterIcon: true,
        onFilterIconClick: onTripFilterIconClick,
        originalIndex: currentIndex++
      });
    }
    
    return configs;
  }, [theme.palette, charts, cartSummaryComponent, demandFilterComponent, availabilityFilterComponent, onServiceFilterComponent, waitingTimeFilterComponent, tripsFilterComponent, onDemandFilterIconClick, onAvailabilityFilterIconClick, onOnServiceFilterIconClick, onWaitingTimeFilterIconClick, onTripFilterIconClick]);

  // Handle card expansion/minimization with useCallback to prevent re-renders
  const handleCardToggle = useCallback((cardId) => {
    setExpandedCards(prev => {
      if (prev.includes(cardId)) {
        // Minimize card - remove from expanded list
        return prev.filter(id => id !== cardId);
      } else {
        if (viewMode === 'single') {
          // In single mode, only allow one expanded card
          return [cardId];
        } else {
          // In multi mode, add to end of expanded list (will appear at top)
          return [...prev, cardId];
        }
      }
    });
  }, [viewMode]);

  // Memoize sorted charts to prevent unnecessary re-calculations
  const sortedCharts = useMemo(() => {
    return [...chartConfigs].sort((a, b) => {
      const aExpanded = expandedCards.includes(a.id);
      const bExpanded = expandedCards.includes(b.id);
      
      if (aExpanded && bExpanded) {
        // Both expanded: sort by expansion order
        return expandedCards.indexOf(a.id) - expandedCards.indexOf(b.id);
      } else if (aExpanded && !bExpanded) {
        // A expanded, B not: A comes first
        return -1;
      } else if (!aExpanded && bExpanded) {
        // B expanded, A not: B comes first
        return 1;
      } else {
        // Both minimized: sort by original index
        return a.originalIndex - b.originalIndex;
      }
    });
  }, [chartConfigs, expandedCards]);

  // If no data is available, show loading or empty state
  if (!charts) {
    return (
      <Box sx={{ width: '100%', textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="text.secondary">
          Loading statistics data...
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <GlobalStyles 
        styles={{
          '@keyframes pulse': {
            '0%': { opacity: 1, transform: 'scale(1)' },
            '50%': { opacity: 0.7, transform: 'scale(1.2)' },
            '100%': { opacity: 1, transform: 'scale(1)' }
          }
        }}
      />
      <Box sx={{ width: '100%' }}>
        {/* Navigation Bar - Horizontal Layout */}
        <Card sx={{ 
          mb: 4, 
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          borderRadius: 3,
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
          border: `1px solid ${theme.palette.grey[200]}`,
        }}>
          <CardContent sx={{ p: 2, px: 3 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              mb: 2
            }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  fontSize: '1.1rem'
                }}
              >
                Statistics Dashboard
              </Typography>
              
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, newValue) => {
                  if (newValue !== null && onViewModeChange) {
                    onViewModeChange(newValue);
                  }
                }}
                aria-label="view mode"
                size="small"
                sx={{ 
                  backgroundColor: theme.palette.grey[100],
                  border: `1px solid ${theme.palette.grey[300]}`,
                  height: 30,
                  '& .MuiToggleButton-root': {
                    color: theme.palette.text.secondary,
                    borderColor: theme.palette.grey[300],
                    padding: '2px 4px',
                    minWidth: 36,
                    height: 30,
                    '&.Mui-selected': {
                      backgroundColor: theme.palette.primary.main,
                      color: 'white',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                      }
                    },
                    '&:hover': {
                      backgroundColor: theme.palette.grey[200],
                    }
                  }
                }}
              >
                <ToggleButton value="multi" aria-label="multi select view">
                  <ViewStreamIcon sx={{ fontSize: 16 }} />
                </ToggleButton>
                <ToggleButton value="single" aria-label="single select view">
                  <WebIcon sx={{ fontSize: 16 }} />
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4,
              width: '100%'
            }}>
              {chartConfigs.map((config) => (
                <NavBarCard
                  key={config.id}
                  config={config}
                  isSelected={expandedCards.includes(config.id)}
                  onToggle={handleCardToggle}
                  theme={theme}
                />
              ))}
            </Box>
          </CardContent>
        </Card>

        {/* Expanded Cards Section */}
        {expandedCards.length > 0 && (
          <Box sx={{ mb: 4 }}>
            {sortedCharts
              .filter(config => expandedCards.includes(config.id))
              .map((config) => (
                <Box key={config.id} sx={{ mb: 3 }}>
                  {config.isCartSummary ? (
                    // Render Cart Summary as a card with minimize functionality
                    <Card sx={{ 
                      boxShadow: '0 12px 48px rgba(0,0,0,0.15)',
                      borderRadius: 4,
                      overflow: 'hidden',
                      border: `2px solid ${config.color}`,
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      background: `linear-gradient(145deg, #ffffff 0%, ${config.color}05 100%)`,
                      '&:hover': {
                        boxShadow: '0 20px 80px rgba(0,0,0,0.2)',
                        transform: 'translateY(-2px)',
                      }
                    }}>
                      <CardContent sx={{ p: 0 }}>
                        {/* Header with Minimize Button */}
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 2,
                          background: `linear-gradient(135deg, ${config.color}15 0%, ${config.color}05 100%)`,
                          borderBottom: `2px solid ${config.color}30`
                        }}>
                          <Typography 
                            variant="h5" 
                            sx={{ 
                              fontWeight: 700,
                              color: config.color,
                              fontSize: '1.3rem',
                              letterSpacing: '0.5px'
                            }}
                          >
                            {config.title}
                          </Typography>
                          <Button
                            startIcon={<CloseIcon/>}
                            onClick={() => handleCardToggle(config.id)}
                            variant="outlined"
                            size="small"
                            sx={{
                              borderColor: config.color,
                              color: config.color,
                              fontWeight: 600,
                              borderRadius: 2,
                              px: 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: 36,
                              '&:hover': {
                                backgroundColor: config.color + '10',
                                borderColor: config.color,
                                transform: 'scale(1.05)'
                              }
                            }}
                          >
                            Minimize
                          </Button>
                        </Box>
                        
                        {/* Cart Summary Content */}
                        <Box sx={{ p: 0 }}>
                          {cartSummaryComponent}
                        </Box>
                      </CardContent>
                    </Card>
                  ) : (
                    // Render regular chart card
                    <ChartCardWithFilters
                      title={config.title}
                      chartData={config.data}
                      filterComponent={config.filterComponent}
                      xAxisTitle={config.data?.xAxisTitle || "Time Period"}
                      yAxisTitle={config.data?.yAxisTitle || "Count"}
                      isExpanded={true}
                      onMinimize={() => handleCardToggle(config.id)}
                      cardColor={config.color}
                      sectionType={config.id}
                      tripTypeOptions={config.id === 'trips' ? tripTypeOptions : []}
                      showFilterIcon={config.showFilterIcon || false}
                      onFilterIconClick={config.onFilterIconClick}
                    />
                  )}
                </Box>
              ))
            }
          </Box>
        )}

        {/* No Selection Message */}
        {expandedCards.length === 0 && (
          <Box sx={{ 
            textAlign: 'center', 
            py: 6,
            color: theme.palette.text.secondary 
          }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Select statistics to view
            </Typography>
            <Typography variant="body2">
              Click on the navigation items above to display detailed statistics charts
            </Typography>
          </Box>
        )}
      </Box>
    </>
  );
}
